type DrSeverityScaleProps = {
  activeScale: string;
};

/**
 * Each level has a primary x position (where the stem + circle lives)
 * and an optional secondary x for range ticks (e.g. High Risk PDR = 71–75).
 */
const SCALE_LEVELS = [
  { scale: 'NORMAL', label: 'Normal', etdrs: '10', x: 100, stemH: 10 },
  { scale: 'VERY_MILD', label: 'Very\nMild', etdrs: '20', x: 185, stemH: 20 },
  { scale: 'MILD', label: 'Mild', etdrs: '35', x: 260, stemH: 35 },
  { scale: 'MODERATE', label: 'Moderate', etdrs: '43', x: 340, stemH: 53 },
  { scale: 'MODERATE_SEVERE', label: 'Moderate\nSevere', etdrs: '47', x: 410, stemH: 57 },
  { scale: 'SEVERE', label: 'Severe', etdrs: '53', x: 480, stemH: 73 },
  { scale: 'MILD_PDR', label: 'Mild\nPDR', etdrs: '61', x: 570, stemH: 81 },
  { scale: 'MODERATE_PDR', label: 'Moderate\nPDR', etdrs: '65', x: 640, stemH: 90 },
  {
    scale: 'HIGH_RISK_PDR',
    label: 'High Risk\nPDR',
    etdrs: '71',
    x: 710,
    stemH: 115,
    secondaryX: 750,
    secondaryEtdrs: '75',
  },
  {
    scale: 'ADVANCED_PDR',
    label: 'Advanced\nPDR',
    etdrs: '85',
    x: 830,
    stemH: 140,
    secondaryX: 790,
    secondaryEtdrs: '81',
  },
];

const BAR_Y = 195;
const BAR_H = 10;
const CIRCLE_R = 4;
const BASE_BAR_H = 24;

// Section divider x positions
const DIVIDER_1_X = 145;
const DIVIDER_2_X = 530;

// "You" indicator constants
const YOU_ARROW_Y = BAR_Y + BAR_H + 20;
const YOU_TEXT_Y = YOU_ARROW_Y + 28;

export function DrSeverityScale({ activeScale }: DrSeverityScaleProps) {
  const activeIndex = SCALE_LEVELS.findIndex(
    (l) => l.scale === activeScale.toUpperCase().replace(/\s+/g, '_')
  );

  return (
    <svg viewBox="0 0 880 280" className="w-full" xmlns="http://www.w3.org/2000/svg">
      {/* Section headers at top */}
      <text x={100} y={18} textAnchor="middle" fontSize="13" fontWeight="bold" fill="#152644">
        Normal
      </text>
      <text x={340} y={18} textAnchor="middle" fontSize="13" fontWeight="bold" fill="#152644">
        NPDR
      </text>
      <text x={710} y={18} textAnchor="middle" fontSize="13" fontWeight="bold" fill="#152644">
        PDR
      </text>

      {/* Dotted vertical dividers between sections */}
      <line
        x1={DIVIDER_1_X}
        y1={8}
        x2={DIVIDER_1_X}
        y2={BAR_Y + BAR_H + 2}
        stroke="#152644"
        strokeWidth="1"
        strokeDasharray="4,3"
      />
      <line
        x1={DIVIDER_2_X}
        y1={8}
        x2={DIVIDER_2_X}
        y2={BAR_Y + BAR_H + 2}
        stroke="#152644"
        strokeWidth="1"
        strokeDasharray="4,3"
      />

      {/* Horizontal colored bar */}
      <rect x={60} y={BAR_Y} width={DIVIDER_1_X - 60} height={BAR_H} fill="#D4E157" />
      <rect
        x={DIVIDER_1_X}
        y={BAR_Y}
        width={DIVIDER_2_X - DIVIDER_1_X}
        height={BAR_H}
        fill="#8cc3e1"
      />
      <rect x={DIVIDER_2_X} y={BAR_Y} width={870 - DIVIDER_2_X} height={BAR_H} fill="#152644" />

      {/* ETDRS label on the left */}
      <text x={8} y={BAR_Y + 8} fontSize="10" fontWeight="bold" fill="#152644">
        ETDRS
      </text>
      <text x={8} y={BAR_Y + BAR_H + 16} fontSize="8" fill="#333">
        Severity Level
      </text>

      {/* Stems, circles, labels */}
      {SCALE_LEVELS.map((level, i) => {
        const isActive = i === activeIndex;
        const isRange = 'secondaryX' in level && level.secondaryX;
        // For range levels, stem/circle/label centered between the two ETDRS positions
        const stemX = isRange ? (level.x + level.secondaryX) / 2 : level.x;
        const circleY = BAR_Y - level.stemH;
        const convergenceY = BAR_Y - BASE_BAR_H;
        const lines = level.label.split('\n');

        return (
          <g key={level.scale}>
            {isRange ? (
              <>
                {/* V-shape: two lines from ETDRS positions converge to center */}
                <line
                  x1={level.x}
                  y1={BAR_Y}
                  x2={stemX}
                  y2={convergenceY}
                  stroke="#152644"
                  strokeWidth="3"
                />
                <line
                  x1={level.secondaryX}
                  y1={BAR_Y}
                  x2={stemX}
                  y2={convergenceY}
                  stroke="#152644"
                  strokeWidth="3"
                />
                {/* Single center stem from convergence point up to circle */}
                <line
                  x1={stemX}
                  y1={convergenceY}
                  x2={stemX}
                  y2={circleY}
                  stroke="#152644"
                  strokeWidth="3"
                />
              </>
            ) : (
              /* Single level: straight stem */
              <line
                x1={level.x}
                y1={BAR_Y}
                x2={level.x}
                y2={circleY}
                stroke="#152644"
                strokeWidth="3"
              />
            )}

            {/* Circle at top of stem */}
            <circle
              cx={stemX}
              cy={circleY}
              r={CIRCLE_R}
              fill="white"
              stroke="#152644"
              strokeWidth="3"
            />

            {/* Label above circle */}
            {lines.map((line, lineIdx) => (
              <text
                key={lineIdx}
                x={stemX}
                y={circleY - CIRCLE_R - 6 - (lines.length - 1 - lineIdx) * 13}
                textAnchor="middle"
                fontSize="11"
                fill="#152644"
                fontWeight={isActive ? 'bold' : 'normal'}
              >
                {line}
              </text>
            ))}

            {/* Primary ETDRS number below bar */}
            <text
              x={level.x}
              y={BAR_Y + BAR_H + 16}
              textAnchor="middle"
              fontSize="10"
              fill="#333"
              fontWeight={isActive ? 'bold' : 'normal'}
            >
              {level.etdrs}
            </text>

            {/* Secondary ETDRS number for range levels */}
            {isRange && (
              <text
                x={level.secondaryX}
                y={BAR_Y + BAR_H + 16}
                textAnchor="middle"
                fontSize="10"
                fill="#333"
              >
                {'secondaryEtdrs' in level ? level.secondaryEtdrs : ''}
              </text>
            )}

            {/* "You" indicator — below the bar, pointing UP */}
            {isActive && (
              <g>
                <polygon
                  points={`${stemX},${YOU_ARROW_Y} ${stemX - 6},${YOU_ARROW_Y + 10} ${stemX + 6},${YOU_ARROW_Y + 10}`}
                  fill="#152644"
                />
                <line
                  x1={stemX}
                  y1={YOU_ARROW_Y + 10}
                  x2={stemX}
                  y2={YOU_ARROW_Y + 18}
                  stroke="#152644"
                  strokeWidth="2"
                />
                <text
                  x={stemX}
                  y={YOU_TEXT_Y}
                  textAnchor="middle"
                  fontSize="12"
                  fontWeight="bold"
                  fill="#152644"
                >
                  You
                </text>
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
}
